import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { InstallBanner } from './components/InstallBanner';
import { ConsentBanner } from './components/ConsentBanner';
import { supabase } from './lib/supabase';
import { Home, Search, Package, Archive, User, Bell, Plus, Share2, Check, Settings, HandHelping, Undo2, Users, X as XIcon, ShieldCheck, HelpCircle, Zap, FolderPlus, Tag, Megaphone, Building2, ScanLine } from 'lucide-react';
import { CodeScanner } from './components/CodeScanner';
import { useAuth } from './store/auth';
import { useGroups } from './store/groups';
import { useSponsors } from './store/sponsors';
import { useOrganizations } from './store/organizations';
import { AuthPage } from './pages/AuthPage';
import { WelcomePage } from './pages/WelcomePage';
import { OnboardingCarousel } from './pages/OnboardingCarousel';
import { ToolsPage } from './pages/ToolsPage';
import { MyToolsPage } from './pages/MyToolsPage';
import { ProfilePage } from './pages/ProfilePage';
import { InventoryPage } from './pages/InventoryPage';
import { HelpPage } from './pages/HelpPage';
import { PersonvernPage } from './pages/PersonvernPage';
import { SponsorDashPage } from './pages/SponsorDashPage';
import { Spinner, BottomSheet, SecondaryBtn, PrimaryBtn, ErrorMsg, NotificationToast, ToastNotification, PullToRefresh } from './components/UI';
import { AdBanner } from './components/AdBanner';
import { FeedbackSheet } from './components/FeedbackSheet';
import { LoanOutSheet } from './components/LoanOutSheet';
import { InviteSheet } from './components/InviteSheet';
import { useInventory } from './store/inventory';
import { isValidInviteCode } from './lib/validation';

type Tab = 'tools' | 'mytools' | 'inventory' | 'profile';

function App() {
  const { user, profile, loading: authLoading, init, mfaEnrolled, updateProfile, passwordRecovery } = useAuth();
  const { groups, setCurrentGroup, fetchGroups, joinByCode, loading: groupLoading, notifications, unreadCount, fetchNotifications, markNotificationsRead, subscribeToNotifications, tools, currentGroupId, approveLoan, rejectLoan, members } = useGroups();
  const { items, myLoans, approveLoanItem, rejectLoanItem, fetchMyLoans, fetchLoanableItems, subscribeToLoanStatus, addInventoryToGroup } = useInventory();
  const { isSponsorAdmin, checkSponsorAdmin } = useSponsors();
  const { groupOrgInfo, fetchMyOrganizations, fetchMyAdminGroups } = useOrganizations();

  const [tab, setTab] = useState<Tab>('tools');
  const [showSearch, setShowSearch] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [showInventorySearch, setShowInventorySearch] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [inventoryAddOpen, setInventoryAddOpen] = useState(false);
  const [inventoryShareOpen, setInventoryShareOpen] = useState(false);
  const [inventoryCategoryOpen, setInventoryCategoryOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showGroupPicker, setShowGroupPicker] = useState(false);
  const [showSelectToolsAfterJoin, setShowSelectToolsAfterJoin] = useState(false);
  const [selectedToolIds, setSelectedToolIds] = useState<string[]>([]);
  const [savingTools, setSavingTools] = useState(false);
  const [myAllTools, setMyAllTools] = useState<any[]>([]);
  const [pendingInviteSheet, setPendingInviteSheet] = useState<string | null>(null);
  const [joinError, setJoinError] = useState('');
  const [joining, setJoining] = useState(false);
  const [mfaRecoveryMsg, setMfaRecoveryMsg] = useState<{ ok: boolean; msg: string } | null>(null);
  const [toastNotification, setToastNotification] = useState<ToastNotification | null>(null);
  const [loanRefreshKey, setLoanRefreshKey] = useState(0);
  const [show2FAPrompt, setShow2FAPrompt] = useState(false);
  const [emailVerifyMsg, setEmailVerifyMsg] = useState<{ ok: boolean; msg: string } | null>(null);
  const [showInventoryManageMenu, setShowInventoryManageMenu] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [inventoryManageListsOpen, setInventoryManageListsOpen] = useState(false);
  const [inventoryTagAdminOpen, setInventoryTagAdminOpen] = useState(false);
  const [inventoryAddListOpen, setInventoryAddListOpen] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [showSponsorDash, setShowSponsorDash] = useState(false);
  const [showQuickLoanOut, setShowQuickLoanOut] = useState(false);
  const [showQuickInvite, setShowQuickInvite] = useState(false);
  const [publicPersonvern] = useState(() => new URLSearchParams(window.location.search).get('view') === 'personvern');
  const [inventoryCodeSearchOpen, setInventoryCodeSearchOpen] = useState(false);
  const [inventoryQuickAddOpen, setInventoryQuickAddOpen] = useState(false);

  // Read URL params on first load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const invite = params.get('invite');
    const email = params.get('email');
    const recoverToken = params.get('recover_mfa');
    const tokenHash = params.get('token_hash');
    const verifyType = params.get('type');
    const codeParam = params.get('code');

    if (invite) {
      const safe = invite.replace(/[^A-Z0-9]/gi, '').substring(0, 10).toUpperCase();
      sessionStorage.setItem('pendingInvite', safe);
    }
    if (email) {
      sessionStorage.setItem('pendingInviteEmail', email.substring(0, 254));
    }
    if (codeParam) {
      const safeCode = codeParam.trim().toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 12);
      if (safeCode) sessionStorage.setItem('pendingItemCode', safeCode);
    }
    if (invite || email || recoverToken || tokenHash || codeParam) {
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (tokenHash) {
      const otpType = (verifyType === 'email' || verifyType === 'signup') ? verifyType : 'email';
      supabase.auth.verifyOtp({ token_hash: tokenHash, type: otpType as any })
        .then(({ error }) => {
          if (error) setEmailVerifyMsg({ ok: false, msg: 'Bekreftelses-lenken er ugyldig eller utløpt. Prøv å registrere deg på nytt.' });
        });
    }

    if (recoverToken && /^[0-9a-f]{48}$/.test(recoverToken)) {
      // Logg alltid ut først — brukeren skal alltid til innloggingssiden
      useAuth.getState().signOut().then(() =>
        fetch('https://hzplqizxzdwmfdopedjf.supabase.co/functions/v1/mfa-recovery', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'verify', token: recoverToken }),
        })
          .then(r => r.json())
          .then(data => setMfaRecoveryMsg(data.error ? { ok: false, msg: data.error } : { ok: true, msg: '' }))
          .catch(() => setMfaRecoveryMsg({ ok: false, msg: 'Noe gikk galt med gjenoppretting.' }))
      );
    }
  }, []);

  // When user is logged in and already has groups, show invite sheet if pending
  useEffect(() => {
    if (user && !groupLoading && groups.length > 0) {
      const code = sessionStorage.getItem('pendingInvite');
      if (code && isValidInviteCode(code)) {
        sessionStorage.removeItem('pendingInvite');
        sessionStorage.removeItem('pendingInviteEmail');
        setPendingInviteSheet(code);
      }
    }
  }, [user, groupLoading, groups.length]);

  // Naviger til Inventar-fanen hvis en ?code= venter på å bli løst (selve
  // oppslaget/navigeringen skjer inni InventoryPage, som eier liste-/detalj-state)
  useEffect(() => {
    if (user && !groupLoading && groups.length > 0 && sessionStorage.getItem('pendingItemCode')) {
      setTab('inventory');
    }
  }, [user, groupLoading, groups.length]);

  const handleInviteJoin = async () => {
    if (!pendingInviteSheet || !user) return;
    setJoining(true); setJoinError('');
    const err = await joinByCode(pendingInviteSheet, user.id);
    if (err) { setJoinError(err); setJoining(false); return; }
    setPendingInviteSheet(null);
    const { data } = await supabase.from('inventory').select('id, navn, emoji, personal_tags').eq('user_id', user.id);
    setMyAllTools((data || []).map((i: any) => ({ ...i, _source: 'inventory' })));
    setShowSelectToolsAfterJoin(true);
    setJoining(false);
  };

  useEffect(() => { init(); }, [init]);

  // Mobilnettlesere fryser/strupter JS-timere når fanen ligger i bakgrunnen
  // (skjermlås, app-bytte). Kjent, dokumentert oppførsel i @supabase/supabase-js:
  // sesjonsfornyelsen bruker en intern lås (navigator LockManager) som kan bli
  // stående låst for alltid hvis nettleseren fryser fanen midt i en operasjon —
  // startAutoRefresh()/stopAutoRefresh() alene løste IKKE dette (8-sekunders
  // «Prøv igjen»-knappen dukket fortsatt opp hver eneste gang, ifølge Roy).
  // v2/v3 gjorde en BLIND reload basert kun på hvor lenge siden hadde vært
  // borte (60 sek, uansett fanebytte/minimering/Alt+Tab via blur/focus) — Roy
  // påpekte at dette er farlig hvis man er midt i et skjema (f.eks. ute for å
  // finne et bilde til «Legg til ting») og bruker over 60 sek: reload sletter
  // alt man har fylt ut, selv om sesjonen egentlig var helt frisk.
  // v4: i stedet for å GJETTE at låsen har satt seg fast basert på tid alene,
  // TESTER vi det faktisk når siden blir synlig igjen — et lite `getSession()`-
  // kall kappes mot en kort tidsfrist. Svarer det raskt (frisk sesjon, det
  // vanlige) skjer ingenting og skjemaet består uansett hvor lenge man var
  // borte. Henger kallet forbi fristen, er det da faktisk bevis for at låsen
  // sitter fast, og først da reloades siden.
  useEffect(() => {
    let hiddenAt: number | null = null;
    let probing = false;
    const MIN_HIDDEN_MS = 5 * 1000; // ikke bry med å teste ved kjapp fane-bytte
    const PROBE_TIMEOUT_MS = 4 * 1000; // en frisk sesjon svarer nesten øyeblikkelig
    const markHidden = () => {
      if (hiddenAt === null) {
        hiddenAt = Date.now();
        supabase.auth.stopAutoRefresh();
      }
    };
    const markVisible = () => {
      supabase.auth.startAutoRefresh();
      const wasHiddenLongEnough = hiddenAt !== null && Date.now() - hiddenAt > MIN_HIDDEN_MS;
      hiddenAt = null;
      if (!wasHiddenLongEnough || probing) return;
      probing = true;
      const timeout = new Promise<'timeout'>(resolve => setTimeout(() => resolve('timeout'), PROBE_TIMEOUT_MS));
      Promise.race([supabase.auth.getSession().then(() => 'ok' as const), timeout]).then(result => {
        probing = false;
        if (result === 'timeout') window.location.reload();
      });
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') markHidden();
      else markVisible();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('blur', markHidden);
    window.addEventListener('focus', markVisible);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('blur', markHidden);
      window.removeEventListener('focus', markVisible);
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchGroups(user.id).then(() => {
      // Hent org-tilknytning for ALLE brukerens grupper med det samme — lar
      // gruppevelgeren seksjonere etter organisasjon selv for grupper eid av en
      // organisasjon brukeren selv ikke er medlem av (kun vanlig gruppemedlem).
      const groupIds = useGroups.getState().groups.map(g => g.id);
      useOrganizations.getState().fetchGroupsOrgInfo(groupIds);
    });
    fetchNotifications(user.id);
    checkSponsorAdmin(user.id);
    fetchMyOrganizations(user.id).then(() => {
      // Hent tilkoblede grupper for alle mine organisasjoner med det samme,
      // slik at «Administrer organisasjon»-arket vet hvilke grupper som er koblet til.
      const orgs = useOrganizations.getState().organizations;
      orgs.forEach(o => useOrganizations.getState().fetchOrgGroups(o.id));
    });
    fetchMyAdminGroups(user.id);
    const uid = user.id;
    const unsubscribeNotifications = subscribeToNotifications(uid, (n) => {
      setToastNotification({
        id: n.id,
        type: n.type,
        message: n.message,
        inventory_id: n.inventory_id ?? null,
        tool_id: n.tool_id ?? null,
      });

      // Direkte store-oppdatering via intern set() — umiddelbar visning uten DB-runde
      const inv = useInventory.getState();
      inv.handleNotification(n.type, n.inventory_id ?? null, n.from_user_id ?? null);

      setLoanRefreshKey(k => k + 1);
      inv.fetchMyLoans(uid);
      inv.fetchMyLentOut(uid);
      inv.fetchItems(uid);
      const activeGroupId = useGroups.getState().currentGroupId;
      if (activeGroupId) inv.fetchLoanableItems(activeGroupId);
    });
    const unsubscribeLoanStatus = subscribeToLoanStatus(user.id);
    return () => { unsubscribeNotifications(); unsubscribeLoanStatus(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (user && groups.length > 0 && !mfaEnrolled && !localStorage.getItem('vtb_2fa_prompt_seen')) {
      setShow2FAPrompt(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, groups.length, mfaEnrolled]);

  // Global polling: oppdater lånestatus hvert 5. sekund når låner har ventende forespørsler
  const hasPendingLoans = myLoans.some(l => l.status === 'pending');
  useEffect(() => {
    if (!user || !hasPendingLoans) return;
    const id = setInterval(() => fetchMyLoans(user.id), 5000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, hasPendingLoans]);

  const handleSetTab = (t: Tab) => { setTab(t); setShowSearch(false); setShowInventorySearch(false); setShowGroupPicker(false); };

  // Skanning fra topbaren: koden går via sessionStorage (samme som ?code=-lenker).
  // Er Inventar allerede åpen, varsler vi den med en hendelse; ellers byttes fanen
  // og Inventar leser koden når den monteres.
  const handleTopbarScan = (code: string) => {
    setScannerOpen(false);
    sessionStorage.setItem('pendingItemCode', code);
    if (tab === 'inventory') window.dispatchEvent(new Event('toolshare:pending-code'));
    else handleSetTab('inventory');
  };

  const openNotifications = () => {
    setShowNotifications(true);
    markNotificationsRead();
  };

  // Sikkerhetsnett mot evig lasteskjerm: hvis init()/fetchGroups aldri fullfører
  // (f.eks. en hengende nettverksforespørsel rett etter at mobilen har vært i
  // bakgrunnen), sto appen tidligere fast på spinneren for alltid — eneste utvei
  // var å vite at man skulle trykke nettleserens egen oppdater-knapp (som ikke
  // en gang er synlig hvis appen er lagt til på hjemskjermen). Viser en
  // «Prøv igjen»-knapp etter 8 sekunder i stedet.
  const stillLoading = authLoading || (groupLoading && groups.length === 0);
  const [loadStuck, setLoadStuck] = useState(false);
  useEffect(() => {
    if (!stillLoading) {
      setLoadStuck(false);
      // Vellykket lasting — nullstill auto-retry-flagget slik at en NY, senere
      // treg innlasting i samme fane også får ett automatisk forsøk (ikke bare
      // aller første gang fanen noensinne var treg).
      sessionStorage.removeItem('ts_auto_retry_done');
      return;
    }
    const t = setTimeout(() => {
      // Roy oppdaget at en helt fersk sideinnlasting (kaldstart, ikke bare
      // tilbakekomst fra bakgrunnen) også kan henge på det aller første
      // getSession()-kallet. Gjør ÉN automatisk oppdatering før den manuelle
      // «Prøv igjen»-knappen vises — sessionStorage-flagget hindrer en
      // reload-løkke hvis problemet vedvarer (f.eks. reelt nettverksbortfall).
      if (!sessionStorage.getItem('ts_auto_retry_done')) {
        sessionStorage.setItem('ts_auto_retry_done', '1');
        window.location.reload();
      } else {
        setLoadStuck(true);
      }
    }, 8000);
    return () => clearTimeout(t);
  }, [stillLoading]);

  if (publicPersonvern) return <Shell><PersonvernPage onClose={() => { window.location.href = 'https://toolshare.no'; }} /></Shell>;
  if (authLoading) return <Shell>{loadStuck ? <LoadStuckFallback /> : <Spinner />}</Shell>;
  if (!user || passwordRecovery) return <Shell><AuthPage recoveryMsg={mfaRecoveryMsg ?? emailVerifyMsg} /></Shell>;
  if (groupLoading && groups.length === 0) return <Shell>{loadStuck ? <LoadStuckFallback /> : <Spinner />}</Shell>;
  if (profile && !profile.has_seen_onboarding) return <Shell><OnboardingCarousel onDone={() => updateProfile({ has_seen_onboarding: true })} /></Shell>;
  if (groups.length === 0) return <Shell><WelcomePage /></Shell>;

  const titles: Record<Tab, string> = {
    tools: 'Hjem',
    search: 'Søk i alle grupper',
    mytools: 'Mine verktøy',
    inventory: 'Inventar',
    profile: 'Profil',
  };

  return (
    <>
    <Shell>
      {/* Topbar */}
      <div className="glass-topbar" style={{ padding: 'calc(var(--safe-top) + 14px) 16px 18px', background: 'var(--green)', borderBottom: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, position: 'relative', zIndex: 10 }}>

        {/* Left: group switcher (kun på Hjem) / sidetittel (andre faner) */}
        <div style={{ position: 'relative', flex: 1, marginRight: 8, minWidth: 0 }}>
          {tab !== 'tools' ? (
            <span style={{ fontSize: 16, fontWeight: 700, letterSpacing: -0.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', color: '#FFFFFF' }}>
              {titles[tab]}
            </span>
          ) : groups.length > 1 ? (
            <button
              onClick={() => setShowGroupPicker(s => !s)}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                background: showGroupPicker ? 'rgba(255,255,255,0.20)' : 'rgba(255,255,255,0.12)',
                border: `1px solid ${showGroupPicker ? 'rgba(255,255,255,0.50)' : 'rgba(255,255,255,0.25)'}`,
                borderRadius: 'var(--radius-sm)', padding: '6px 10px',
                cursor: 'pointer', maxWidth: '100%',
              }}
            >
              {groupOrgInfo[currentGroupId || '']
                ? <Building2 size={13} color="rgba(255,255,255,0.85)" strokeWidth={2} style={{ flexShrink: 0 }} />
                : <Users size={13} color="rgba(255,255,255,0.85)" strokeWidth={2} style={{ flexShrink: 0 }} />}
              <span style={{ fontSize: 14, fontWeight: 700, color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {groups.find(g => g.id === currentGroupId)?.name || 'Gruppe'}
              </span>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.70)', flexShrink: 0, marginTop: 1 }}>
                {showGroupPicker ? '▴' : '▾'}
              </span>
            </button>
          ) : (
            <span style={{ fontSize: 16, fontWeight: 700, letterSpacing: -0.3, display: 'flex', alignItems: 'center', gap: 5, color: '#FFFFFF' }}>
              {groupOrgInfo[currentGroupId || '']
                ? <Building2 size={14} color="rgba(255,255,255,0.85)" strokeWidth={2} style={{ flexShrink: 0 }} />
                : <Users size={14} color="rgba(255,255,255,0.85)" strokeWidth={2} style={{ flexShrink: 0 }} />}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{groups.find(g => g.id === currentGroupId)?.name || 'Gruppe'}</span>
            </span>
          )}

          {tab === 'tools' && showGroupPicker && (
            <>
              <div onClick={() => setShowGroupPicker(false)} style={{ position: 'fixed', inset: 0, zIndex: 149 }} />
              <div style={{
                position: 'absolute', top: 'calc(100% + 8px)', left: 0, zIndex: 150,
                background: 'var(--surface-solid)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius)', boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                minWidth: 220, overflow: 'hidden',
              }}>
                {(() => {
                  // Seksjonerer etter groupOrgInfo (per-gruppe org-tilknytning) i stedet for
                  // brukerens EGNE organisasjoner — fungerer dermed også for grupper eid av en
                  // organisasjon brukeren selv kun er vanlig gruppemedlem i (ikke org-medlem).
                  const personalGroups = groups.filter(g => !groupOrgInfo[g.id]);
                  const orgSections = new Map<string, { label: string; items: typeof groups }>();
                  groups.forEach(g => {
                    const org = groupOrgInfo[g.id];
                    if (!org) return;
                    if (!orgSections.has(org.id)) orgSections.set(org.id, { label: org.name, items: [] });
                    orgSections.get(org.id)!.items.push(g);
                  });
                  const sections: { label: string; isOrg: boolean; items: typeof groups }[] = [
                    { label: 'Dine grupper', isOrg: false, items: personalGroups },
                    ...Array.from(orgSections.values()).map(s => ({ ...s, isOrg: true })),
                  ];
                  return sections.filter(s => s.items.length > 0).map((section, sIdx) => (
                    <div key={section.label} style={{ borderTop: sIdx === 0 ? 'none' : '1px solid rgba(255,255,255,0.12)' }}>
                      <div style={{
                        padding: '9px 14px', display: 'flex', alignItems: 'center', gap: 6,
                        fontSize: 11.5, fontWeight: 700, color: 'var(--green-dark)', textTransform: 'uppercase', letterSpacing: '0.07em',
                        background: 'var(--green-light)',
                      }}>
                        {section.isOrg ? <Building2 size={12} strokeWidth={2.5} /> : <Users size={12} strokeWidth={2.5} />}
                        {section.label}
                      </div>
                      {section.items.map(g => (
                        <button
                          key={g.id}
                          onClick={() => { setShowGroupPicker(false); setCurrentGroup(g.id); }}
                          style={{
                            width: '100%', padding: '12px 14px',
                            border: 'none', borderTop: '1px solid rgba(255,255,255,0.07)',
                            background: g.id === currentGroupId ? 'var(--green-light)' : 'transparent',
                            color: g.id === currentGroupId ? 'var(--green)' : 'var(--text)',
                            fontSize: 14, fontWeight: g.id === currentGroupId ? 600 : 400,
                            textAlign: 'left', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                          }}
                        >
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{g.name}</span>
                          {g.id === currentGroupId && <Check size={15} color="var(--green-dark)" strokeWidth={2.5} />}
                        </button>
                      ))}
                    </div>
                  ));
                })()}
              </div>
            </>
          )}
        </div>

        {/* Right: actions + bell */}
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0 }}>
          <IBtn onClick={() => setScannerOpen(true)}><ScanLine size={20} color="rgba(255,255,255,0.85)" strokeWidth={1.8} /></IBtn>
          {tab === 'tools' && <IBtn onClick={() => setShowSearch(s => !s)}><Search size={20} color="rgba(255,255,255,0.85)" strokeWidth={1.8} /></IBtn>}
          {tab === 'inventory' && <IBtn onClick={() => setShowInventorySearch(s => !s)}><Search size={20} color={showInventorySearch ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.85)'} strokeWidth={1.8} /></IBtn>}
          {tab === 'inventory' && <IBtn onClick={() => setInventoryShareOpen(true)}><Share2 size={20} color="rgba(255,255,255,0.85)" strokeWidth={1.8} /></IBtn>}
          {tab === 'inventory' && (
            <div style={{ position: 'relative' }}>
              <IBtn onClick={() => setShowInventoryManageMenu(s => !s)}>
                <Settings size={20} color={showInventoryManageMenu ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.85)'} strokeWidth={1.8} />
              </IBtn>
              {showInventoryManageMenu && (
                <>
                  <div onClick={() => setShowInventoryManageMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 149 }} />
                  <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 150, background: 'var(--surface-solid)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', minWidth: 200, overflow: 'hidden' }}>
                    {[
                      { label: 'Administrer lister', action: () => { setInventoryManageListsOpen(true); setShowInventoryManageMenu(false); } },
                      ...(members.find(m => m.user_id === user?.id)?.role === 'admin' ? [{ label: 'Administrer kategorier', action: () => { setInventoryCategoryOpen(true); setShowInventoryManageMenu(false); } }] : []),
                      { label: 'Administrer tagger', action: () => { setInventoryTagAdminOpen(true); setShowInventoryManageMenu(false); } },
                      { label: 'Slå opp kode', action: () => { setInventoryCodeSearchOpen(true); setShowInventoryManageMenu(false); } },
                    ].map((item, i) => (
                      <button key={i} onClick={item.action} style={{ width: '100%', padding: '12px 16px', border: 'none', borderTop: i === 0 ? 'none' : '1px solid rgba(255,255,255,0.07)', background: 'transparent', color: 'var(--text)', fontSize: 14, fontWeight: 400, textAlign: 'left', cursor: 'pointer' }}>
                        {item.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          <button onClick={openNotifications} style={{ background: 'none', border: 'none', padding: 7, borderRadius: 'var(--radius-xs)', cursor: 'pointer', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Bell size={22} color="rgba(255,255,255,0.85)" strokeWidth={1.8} />
            {unreadCount > 0 && (
              <span style={{ position: 'absolute', top: 2, right: 2, width: 16, height: 16, background: 'var(--red)', color: '#fff', borderRadius: '50%', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          <button onClick={() => setHelpOpen(true)} style={{ background: 'none', border: 'none', padding: 7, borderRadius: 'var(--radius-xs)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <HelpCircle size={22} color="rgba(255,255,255,0.85)" strokeWidth={1.8} />
          </button>
        </div>
      </div>

      {/* Content */}
      <PullToRefresh id={tab === 'profile' ? 'profile-content' : undefined} style={{ flex: 1, padding: 16 }}>
        {tab === 'tools' && <ToolsPage showSearch={showSearch} />}
        {tab === 'mytools' && <MyToolsPage key={loanRefreshKey} addOpen={addOpen} onCloseAdd={() => setAddOpen(false)} />}
        {tab === 'inventory' && <InventoryPage
          addOpen={inventoryAddOpen} onCloseAdd={() => setInventoryAddOpen(false)}
          shareOpen={inventoryShareOpen} onCloseShare={() => setInventoryShareOpen(false)}
          categoryOpen={inventoryCategoryOpen} onCloseCategory={() => setInventoryCategoryOpen(false)}
          manageListsOpen={inventoryManageListsOpen} onCloseManageLists={() => setInventoryManageListsOpen(false)}
          tagAdminOpen={inventoryTagAdminOpen} onCloseTagAdmin={() => setInventoryTagAdminOpen(false)}
          addListOpen={inventoryAddListOpen} onCloseAddList={() => setInventoryAddListOpen(false)}
          codeSearchOpen={inventoryCodeSearchOpen} onCloseCodeSearch={() => setInventoryCodeSearchOpen(false)}
          quickAddOpen={inventoryQuickAddOpen} onCloseQuickAdd={() => setInventoryQuickAddOpen(false)}
          searchOpen={showInventorySearch}
        />}
        {tab === 'profile' && <ProfilePage onJoined={async () => {
          const { data } = await supabase.from('inventory').select('id, navn, emoji, personal_tags').eq('user_id', user.id);
          setMyAllTools((data || []).map((i: any) => ({ ...i, _source: 'inventory' })));
          setShowSelectToolsAfterJoin(true);
        }} onOpenFeedback={() => setFeedbackOpen(true)} />}
      </PullToRefresh>

      {/* Annonse — fast plassert rett over bunnmenyen, kun på Hjem-fanen.
          NB: bevisst deaktivert (AdBanner rendrer <ins> kun når slot/client er satt) inntil
          Google har godkjent AdSense-kontoen fullt ut — se [[project_verktoybanken]]-minnet
          for hvorfor: en tidligere versjon frøs scroll/klikk i hele appen mens kontoen ventet
          på godkjenning. Skru på ved å sette REACT_APP_ADSENSE_SLOT_OVERSIKT i .env.local/Netlify. */}
      {tab === 'tools' && (
        <div style={{ padding: '0 16px', background: 'var(--bg)', flexShrink: 0 }}>
          <AdBanner slot={process.env.REACT_APP_ADSENSE_SLOT_OVERSIKT || ''} />
        </div>
      )}

      {/* Bottom nav — full bredde, med hevet +-knapp midt på */}
      <div style={{ position: 'relative', background: 'var(--green)', display: 'flex', alignItems: 'center', padding: '4px 4px calc(var(--safe-bottom) + 4px)', flexShrink: 0 }}>
        {([
          { id: 'tools',     Icon: Home,    label: 'Hjem' },
          { id: 'mytools',   Icon: Package, label: 'Mine' },
        ] as { id: Tab; Icon: React.FC<any>; label: string }[]).map(t => {
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => handleSetTab(t.id)} style={{ flex: 1, border: 'none', background: 'none', padding: '6px 4px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
              <t.Icon size={20} color={active ? '#fff' : 'rgba(255,255,255,0.6)'} strokeWidth={active ? 2.4 : 1.8} />
              <span style={{ fontSize: 10, color: active ? '#fff' : 'rgba(255,255,255,0.55)', fontWeight: active ? 700 : 400 }}>{t.label}</span>
            </button>
          );
        })}

        <div style={{ width: 64, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
          <button
            onClick={() => setShowQuickAdd(true)}
            style={{
              width: 52, height: 52, borderRadius: '50%', border: 'none', cursor: 'pointer',
              background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 14px rgba(0,0,0,0.35)', transform: 'translateY(-16px)', flexShrink: 0,
            }}
          >
            <Plus size={26} color="#fff" strokeWidth={2.5} />
          </button>
        </div>

        {([
          { id: 'inventory', Icon: Archive, label: 'Inventar' },
          { id: 'profile',   Icon: User,    label: 'Profil' },
        ] as { id: Tab; Icon: React.FC<any>; label: string }[]).map(t => {
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => handleSetTab(t.id)} style={{ flex: 1, border: 'none', background: 'none', padding: '6px 4px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
              <t.Icon size={20} color={active ? '#fff' : 'rgba(255,255,255,0.6)'} strokeWidth={active ? 2.4 : 1.8} />
              <span style={{ fontSize: 10, color: active ? '#fff' : 'rgba(255,255,255,0.55)', fontWeight: active ? 700 : 400 }}>{t.label}</span>
            </button>
          );
        })}
      </div>

      {showQuickAdd && (
        <BottomSheet title="Hurtighandling" onClose={() => setShowQuickAdd(false)}>
          <button
            onClick={() => { setTab('inventory'); setInventoryAddOpen(true); setShowQuickAdd(false); }}
            style={{ width: '100%', padding: '13px 16px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--surface)', fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
          >
            <div style={{ width: 34, height: 34, borderRadius: 'var(--radius-sm)', background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Archive size={17} color="var(--green-dark)" strokeWidth={1.8} />
            </div>
            <span style={{ fontWeight: 600 }}>Legg til ny ting</span>
          </button>
          <button
            onClick={() => { setTab('inventory'); setInventoryQuickAddOpen(true); setShowQuickAdd(false); }}
            style={{ width: '100%', padding: '13px 16px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--surface)', fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
          >
            <div style={{ width: 34, height: 34, borderRadius: 'var(--radius-sm)', background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Zap size={17} color="var(--green-dark)" strokeWidth={1.8} />
            </div>
            <span style={{ fontWeight: 600 }}>Hurtigregistrering</span>
          </button>
          <button
            onClick={() => { setTab('inventory'); setInventoryAddListOpen(true); setShowQuickAdd(false); }}
            style={{ width: '100%', padding: '13px 16px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--surface)', fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
          >
            <div style={{ width: 34, height: 34, borderRadius: 'var(--radius-sm)', background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <FolderPlus size={17} color="var(--green-dark)" strokeWidth={1.8} />
            </div>
            <span style={{ fontWeight: 600 }}>Legg til ny liste</span>
          </button>
          {members.find(m => m.user_id === user?.id)?.role === 'admin' && (
            <button
              onClick={() => { setTab('inventory'); setInventoryCategoryOpen(true); setShowQuickAdd(false); }}
              style={{ width: '100%', padding: '13px 16px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--surface)', fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
            >
              <div style={{ width: 34, height: 34, borderRadius: 'var(--radius-sm)', background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Tag size={17} color="var(--green-dark)" strokeWidth={1.8} />
              </div>
              <span style={{ fontWeight: 600 }}>Legg til ny kategori</span>
            </button>
          )}
          <button
            onClick={() => { setShowQuickLoanOut(true); setShowQuickAdd(false); }}
            style={{ width: '100%', padding: '13px 16px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--surface)', fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
          >
            <div style={{ width: 34, height: 34, borderRadius: 'var(--radius-sm)', background: 'var(--amber-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <HandHelping size={17} color="var(--amber)" strokeWidth={1.8} />
            </div>
            <span style={{ fontWeight: 600 }}>Lån ut et verktøy</span>
          </button>
          <button
            onClick={() => { setShowQuickInvite(true); setShowQuickAdd(false); }}
            style={{ width: '100%', padding: '13px 16px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--surface)', fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: isSponsorAdmin ? 8 : 0, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
          >
            <div style={{ width: 34, height: 34, borderRadius: 'var(--radius-sm)', background: 'var(--blue-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Users size={17} color="var(--blue)" strokeWidth={1.8} />
            </div>
            <span style={{ fontWeight: 600 }}>Inviter noen</span>
          </button>
          {isSponsorAdmin && (
            <button
              onClick={() => { setShowSponsorDash(true); setShowQuickAdd(false); }}
              style={{ width: '100%', padding: '13px 16px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--surface)', fontSize: 14, fontWeight: 500, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
            >
              <div style={{ width: 34, height: 34, borderRadius: 'var(--radius-sm)', background: 'var(--accent-light, var(--green-light))', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Megaphone size={17} color="var(--accent)" strokeWidth={1.8} />
              </div>
              <span style={{ fontWeight: 600 }}>Sponsor-dashbord</span>
            </button>
          )}
        </BottomSheet>
      )}

      {showQuickLoanOut && <LoanOutSheet onClose={() => setShowQuickLoanOut(false)} />}
      {showQuickInvite && <InviteSheet onClose={() => setShowQuickInvite(false)} />}

      {/* Notifications sheet */}
      {showNotifications && (
        <BottomSheet title="Varsler" onClose={() => setShowNotifications(false)}>
          {notifications.length === 0 && (
            <div style={{ textAlign: 'center', padding: '32px 0 16px', color: 'var(--text3)' }}>
              <Bell size={32} strokeWidth={1.5} style={{ marginBottom: 10 }} />
              <p style={{ fontSize: 14 }}>Ingen varsler enda</p>
            </div>
          )}
          {notifications.map(n => {
            const isPendingToolRequest = n.type === 'loan_request' && n.tool_id &&
              tools.find(t => t.id === n.tool_id)?.status === 'pending' &&
              notifications.find(x => x.type === 'loan_request' && x.tool_id === n.tool_id)?.id === n.id;
            const isPendingInventoryRequest = n.type === 'loan_request' && n.inventory_id &&
              items.find(i => i.id === n.inventory_id)?.status === 'pending' &&
              notifications.find(x => x.type === 'loan_request' && x.inventory_id === n.inventory_id)?.id === n.id;
            const isPending = isPendingToolRequest || isPendingInventoryRequest;

            const typeIcon = () => {
              switch (n.type) {
                case 'loan_request':  return <div style={{ width: 34, height: 34, borderRadius: 10, background: 'var(--blue-light)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><HandHelping size={16} color="var(--blue)" strokeWidth={2} /></div>;
                case 'loan_approved': return <div style={{ width: 34, height: 34, borderRadius: 10, background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Check size={16} color="var(--green)" strokeWidth={2.5} /></div>;
                case 'loan_rejected': return <div style={{ width: 34, height: 34, borderRadius: 10, background: 'var(--red-light)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><XIcon size={16} color="var(--red)" strokeWidth={2.5} /></div>;
                case 'returned':      return <div style={{ width: 34, height: 34, borderRadius: 10, background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Undo2 size={16} color="var(--green)" strokeWidth={2} /></div>;
                case 'joined':        return <div style={{ width: 34, height: 34, borderRadius: 10, background: 'var(--purple-light)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Users size={16} color="var(--purple)" strokeWidth={2} /></div>;
                default:              return <div style={{ width: 34, height: 34, borderRadius: 10, background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Bell size={16} color="var(--text3)" strokeWidth={2} /></div>;
              }
            };

            return (
              <div key={n.id} style={{ marginBottom: 10, padding: '12px', borderRadius: 'var(--radius-sm)', background: !n.read ? 'var(--bg)' : 'transparent', border: '1px solid ' + (!n.read ? 'var(--border)' : 'transparent') }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  {typeIcon()}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: !n.read || isPending ? 600 : 400, lineHeight: 1.4, whiteSpace: 'pre-line' }}>{n.message}</div>
                    <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 3 }}>
                      {new Date(n.created_at).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
                {isPending && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button onClick={async () => {
                      if (isPendingToolRequest && n.tool_id) { await approveLoan(n.tool_id); }
                      else if (isPendingInventoryRequest && n.inventory_id) { await approveLoanItem(n.inventory_id); fetchMyLoans(user!.id); }
                      fetchNotifications(user!.id);
                    }} style={{ flex: 1, padding: '9px 0', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text)', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                      <Check size={13} strokeWidth={2.5} /> Godkjenn
                    </button>
                    <button onClick={async () => {
                      if (isPendingToolRequest && n.tool_id) { await rejectLoan(n.tool_id); }
                      else if (isPendingInventoryRequest && n.inventory_id) { await rejectLoanItem(n.inventory_id); }
                      fetchNotifications(user!.id);
                    }} style={{ flex: 1, padding: '9px 0', borderRadius: 'var(--radius-sm)', border: '1px solid var(--red)', background: 'none', color: 'var(--red)', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                      <XIcon size={13} strokeWidth={2.5} /> Avvis
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          <div style={{ marginTop: 6 }}>
            <SecondaryBtn onClick={() => setShowNotifications(false)}>Lukk</SecondaryBtn>
          </div>
        </BottomSheet>
      )}

      {/* Toast-notifikasjon */}
      {toastNotification && (
        <NotificationToast
          notification={toastNotification}
          onDismiss={() => setToastNotification(null)}
          onApprove={
            toastNotification.type === 'loan_request'
              ? async () => {
                  if (toastNotification.tool_id) await approveLoan(toastNotification.tool_id);
                  else if (toastNotification.inventory_id) { await approveLoanItem(toastNotification.inventory_id); fetchMyLoans(user!.id); }
                  fetchNotifications(user!.id);
                }
              : undefined
          }
          onReject={
            toastNotification.type === 'loan_request'
              ? async () => {
                  if (toastNotification.tool_id) await rejectLoan(toastNotification.tool_id);
                  else if (toastNotification.inventory_id) await rejectLoanItem(toastNotification.inventory_id);
                  fetchNotifications(user!.id);
                }
              : undefined
          }
        />
      )}

      {/* 2FA-prompt — vises én gang for brukere uten 2FA */}
      {show2FAPrompt && ReactDOM.createPortal(
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: 'var(--surface-solid)', borderRadius: 'var(--radius)', padding: '28px 24px 24px', width: '100%', maxWidth: 380, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ width: 44, height: 44, borderRadius: 'var(--radius-sm)', background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <ShieldCheck size={22} color="var(--green-dark)" strokeWidth={1.8} />
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>Sikre kontoen din</div>
                <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 2 }}>2-faktor autentisering</div>
              </div>
            </div>
            <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 22 }}>
              Beskytt kontoen din med en ekstra sikkerhetskode. Det tar under ett minutt å sette opp.
            </p>
            <button
              onClick={() => { localStorage.setItem('vtb_2fa_prompt_seen', 'dismissed'); setShow2FAPrompt(false); handleSetTab('profile'); }}
              style={{ width: '100%', padding: 13, background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)', color: '#0A1208', border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 15, fontWeight: 700, cursor: 'pointer', marginBottom: 10, boxShadow: '0 4px 16px rgba(34,197,94,0.35)' }}
            >
              Aktiver nå
            </button>
            <button
              onClick={() => { localStorage.setItem('vtb_2fa_prompt_seen', 'dismissed'); setShow2FAPrompt(false); }}
              style={{ width: '100%', padding: 13, background: 'none', color: 'var(--text2)', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', fontSize: 14, cursor: 'pointer' }}
            >
              Ikke nå
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Pending invite from email link */}
      {pendingInviteSheet && (
        <BottomSheet title="Du er invitert! 🔑" onClose={() => setPendingInviteSheet(null)}>
          <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 20 }}>
            Du har mottatt en invitasjon. Klikk på knappen under for å bli med i gruppen.
          </p>
          <div style={{ background: 'var(--bg)', borderRadius: 'var(--radius-sm)', padding: '14px', textAlign: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>Invitasjonskode</div>
            <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: 6 }}>{pendingInviteSheet}</div>
          </div>
          {joinError && <ErrorMsg msg={joinError} />}
          <PrimaryBtn onClick={handleInviteJoin} disabled={joining}>
            {joining ? 'Kobler til...' : 'Bli med i gruppen'}
          </PrimaryBtn>
          <SecondaryBtn onClick={() => setPendingInviteSheet(null)}>Avbryt</SecondaryBtn>
        </BottomSheet>
      )}

      {/* Select tools after joining a group */}
      {showSelectToolsAfterJoin && ReactDOM.createPortal(
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div style={{ background: 'var(--surface-solid)', borderRadius: '20px 20px 0 0', padding: '18px 16px calc(env(safe-area-inset-bottom, 0px) + 32px)', width: '100%', maxWidth: 520, maxHeight: '85vh', overflowY: 'auto', overscrollBehavior: 'contain', position: 'relative' }}>
            <button onClick={() => { setShowSelectToolsAfterJoin(false); setSelectedToolIds([]); }} style={{ position: 'absolute', top: 16, right: 16, width: 32, height: 32, borderRadius: '50%', border: 'none', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text3)', fontSize: 18, lineHeight: 1 }}>×</button>
            <div style={{ width: 36, height: 4, background: 'var(--border-strong)', borderRadius: 2, margin: '0 auto 16px' }} />
            <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 16 }}>Del verktøy med gruppen</h2>
            <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 16 }}>
              Velg hvilke av dine verktøy du vil dele i den nye gruppen. Du kan endre dette senere.
            </p>

        {myAllTools.length > 0 && (
              <button
                onClick={() => setSelectedToolIds(
                  selectedToolIds.length === myAllTools.length ? [] : myAllTools.map((t: any) => t.id)
                )}
                style={{ width: '100%', padding: '10px 14px', marginBottom: 12, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-strong)', background: 'none', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}
              >
                {selectedToolIds.length === myAllTools.length ? '⬜ Fjern alle' : '✅ Velg alle'}
              </button>
            )}

            {myAllTools.length === 0 && (
              <p style={{ textAlign: 'center', color: 'var(--text3)', padding: '16px 0' }}>
                Du har ingen ting i inventaret enda. Legg til ting under «Inventar»-fanen, så kan du dele dem med gruppen.
              </p>
            )}
            {myAllTools.map((tool: any) => (
              <div
                key={tool.id}
                onClick={() => setSelectedToolIds((prev: string[]) =>
                  prev.includes(tool.id) ? prev.filter((id: string) => id !== tool.id) : [...prev, tool.id]
                )}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 14px', marginBottom: 8,
                  border: `1px solid ${selectedToolIds.includes(tool.id) ? 'var(--green)' : 'var(--border)'}`,
                  borderRadius: 'var(--radius)',
                  background: selectedToolIds.includes(tool.id) ? 'var(--green-light)' : 'var(--surface)',
                  cursor: 'pointer',
                }}
              >
                <span style={{ fontSize: 24 }}>{tool.emoji}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{tool.navn}</div>
                  <div style={{ fontSize: 13, color: 'var(--text2)' }}>{(tool.personal_tags || []).join(', ')}</div>
                </div>
                <span style={{ fontSize: 20 }}>{selectedToolIds.includes(tool.id) ? '✅' : '⬜'}</span>
              </div>
            ))}
            <button
              onClick={async () => {
              setSavingTools(true);
              for (const toolId of selectedToolIds) {
                await supabase.from('inventory').update({ loanable: true }).eq('id', toolId);
                await addInventoryToGroup(toolId, currentGroupId!);
              }
              setSavingTools(false);
              setShowSelectToolsAfterJoin(false);
              setSelectedToolIds([]);
              if (currentGroupId) await fetchLoanableItems(currentGroupId);
            }}
              style={{ width: '100%', padding: 13, background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)', color: '#0A1208', border: 'none', borderRadius: 'var(--radius-sm)', fontSize: 15, fontWeight: 700, cursor: 'pointer', marginBottom: 10, boxShadow: '0 4px 16px rgba(34,197,94,0.35)' }}
            >
              {savingTools ? 'Lagrer...' : `Del ${selectedToolIds.length} verktøy med gruppen`}
            </button>
            <button
              onClick={() => { setShowSelectToolsAfterJoin(false); setSelectedToolIds([]); }}
              style={{ width: '100%', padding: 13, background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', fontSize: 15, fontWeight: 500, cursor: 'pointer' }}
            >
              Hopp over
            </button>
          </div>
        </div>,
        document.body
      )}
      {helpOpen && <HelpPage onClose={() => setHelpOpen(false)} onOpenFeedback={() => { setHelpOpen(false); setFeedbackOpen(true); }} />}
      {scannerOpen && <CodeScanner onScan={handleTopbarScan} onClose={() => setScannerOpen(false)} />}
      {showSponsorDash && <SponsorDashPage onClose={() => setShowSponsorDash(false)} />}
      <FeedbackSheet open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </Shell>
    <InstallBanner />
    <ConsentBanner />
    </>
  );
}

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="app-shell" style={{ display: 'flex', flexDirection: 'column', maxWidth: 480, margin: '0 auto', background: 'transparent', position: 'relative', overflow: 'hidden' }}>
    {children}
  </div>
);

const LoadStuckFallback: React.FC = () => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 32px', textAlign: 'center', gap: 16 }}>
    <Spinner />
    <div style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.5 }}>
      Dette tar lengre tid enn vanlig. Kan skje rett etter at mobilen har vært i bakgrunnen eller mistet nett en kort stund.
    </div>
    <button
      onClick={() => window.location.reload()}
      style={{ padding: '11px 22px', borderRadius: 'var(--radius-sm)', border: 'none', background: 'var(--green)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
    >
      Prøv igjen
    </button>
  </div>
);

const IBtn: React.FC<{ onClick: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <button onClick={onClick} style={{ background: 'none', border: 'none', padding: 7, borderRadius: 'var(--radius-xs)', fontSize: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{children}</button>
);

export default App;